import React from "react";
import { useTranslation } from "react-i18next";
import {
  Bluetooth,
  Cable,
  Radio,
  Smartphone,
  Wifi,
  WifiOff,
} from "lucide-react";
import { networkSummary } from "utils/caseDashboard";

const VIA_ICON = { wifi: Wifi, field: Radio, ethernet: Cable, none: WifiOff };

/** Four bars, filled by Wi-Fi signal (0-100). */
export const SignalBars = ({ signal }) => {
  const filled = signal == null ? 0 : Math.ceil((signal / 100) * 4);
  return (
    <span className="inline-flex items-end gap-[2px]" aria-hidden>
      {[1, 2, 3, 4].map((n) => (
        <span
          key={n}
          className={`w-[3px] rounded-sm ${
            n <= filled ? "bg-[#10B981]" : "bg-[#E5E7EB]"
          }`}
          style={{ height: 3 + n * 2.5 }}
        />
      ))}
    </span>
  );
};

/** "Wi-Fi: Office · 68%", "Own network: SIT-Case-3", "Ethernet", "No network". */
export const viaText = (t, sum) => {
  if (!sum) return null;
  const base = t(`caseDashboard.connections.via.${sum.via}`, {
    ssid: sum.ssid || "?",
  });
  return sum.signal != null ? `${base} · ${sum.signal}%` : base;
};

const Row = ({ icon: Icon, title, detail, right }) => (
  <div className="flex items-center gap-3 py-2">
    <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-[#F3F4F6]">
      <Icon size={15} className="text-[#6B7280]" />
    </span>
    <div className="min-w-0 flex-1">
      <p className="truncate text-[13px] font-medium text-[#111827]">{title}</p>
      {detail && (
        <p className="truncate font-mono text-[11px] text-[#6B7280]">
          {detail}
        </p>
      )}
    </div>
    {right}
  </div>
);

/** The case's network (IP addresses, Wi-Fi) and the phones connected now. */
const ConnectionsCard = ({ network, bluetooth }) => {
  const { t } = useTranslation();
  const net = network.data;
  const sum = networkSummary(net);
  const bt = bluetooth.data;
  const phones = bt?.connected || [];
  const adapters = bt?.adapters || [];
  const ViaIcon = VIA_ICON[sum?.via] || WifiOff;

  return (
    <div className="grid gap-4 lg:grid-cols-2">
      <section className="rounded-xl border border-[#F3F4F6] bg-white p-4">
        <h2 className="mb-1 flex items-center gap-2 text-[13px] font-semibold text-[#111827]">
          <Wifi size={15} className="text-[#9CA3AF]" />
          {t("caseDashboard.connections.network")}
        </h2>
        {!net ? (
          <p className="py-2 text-[13px] text-[#6B7280]">
            {network.error
              ? t("caseDashboard.connections.unavailable")
              : t("caseDashboard.common.loading")}
          </p>
        ) : (
          <div className="divide-y divide-[#F3F4F6]">
            <Row
              icon={ViaIcon}
              title={viaText(t, { ...sum, signal: null })}
              detail={
                sum.via === "wifi" && sum.signal != null
                  ? t("caseDashboard.connections.signal", {
                      signal: sum.signal,
                    })
                  : null
              }
              right={sum.via === "wifi" && <SignalBars signal={sum.signal} />}
            />
            {(net.interfaces?.length
              ? net.interfaces
              : net.ip
              ? [{ name: "wlan0", ip: net.ip }]
              : []
            ).map((i) => (
              <Row
                key={`${i.name}-${i.ip}`}
                icon={i.name?.startsWith("wlan") ? Wifi : Cable}
                title={i.ip}
                detail={t(`caseDashboard.connections.iface`, {
                  name: i.name,
                  kind: i.name?.startsWith("wlan")
                    ? t("caseDashboard.connections.kindWifi")
                    : t("caseDashboard.connections.kindCable"),
                })}
              />
            ))}
            {net.ap_ssid && sum.via !== "field" && (
              <Row
                icon={Radio}
                title={t("caseDashboard.connections.fieldNetwork", {
                  ssid: net.ap_ssid,
                })}
                detail={
                  net.ap_configured
                    ? t("caseDashboard.connections.fieldReady")
                    : t("caseDashboard.connections.fieldNoPassword")
                }
              />
            )}
          </div>
        )}
      </section>

      <section className="rounded-xl border border-[#F3F4F6] bg-white p-4">
        <h2 className="mb-1 flex items-center gap-2 text-[13px] font-semibold text-[#111827]">
          <Bluetooth size={15} className="text-[#9CA3AF]" />
          {t("caseDashboard.connections.bluetooth")}
          {bt && (
            <span className="rounded bg-[#F3F4F6] px-1.5 py-0.5 text-[11px] font-medium text-[#6B7280]">
              {t("caseDashboard.connections.phonesCount", {
                count: phones.length,
              })}
            </span>
          )}
        </h2>
        {!bt ? (
          <p className="py-2 text-[13px] text-[#6B7280]">
            {bluetooth.error
              ? t("caseDashboard.connections.needsUpdate")
              : t("caseDashboard.common.loading")}
          </p>
        ) : (
          <>
            {bt.error && (
              <p className="py-2 text-[13px] text-[#C2611F]">{bt.error}</p>
            )}
            {phones.length === 0 && !bt.error ? (
              <p className="py-2 text-[13px] text-[#6B7280]">
                {t("caseDashboard.connections.noPhones")}
              </p>
            ) : (
              <div className="divide-y divide-[#F3F4F6]">
                {phones.map((p) => (
                  <Row
                    key={p.address}
                    icon={
                      p.icon === "phone" || !p.icon ? Smartphone : Bluetooth
                    }
                    title={p.name || t("caseDashboard.connections.unnamed")}
                    detail={[p.address, p.adapter].filter(Boolean).join(" · ")}
                  />
                ))}
              </div>
            )}
            {adapters.length > 0 && (
              <p className="mt-2 text-[11px] text-[#9CA3AF]">
                {adapters
                  .map(
                    (a) =>
                      `${a.id} ${a.name || ""}${
                        a.powered
                          ? ""
                          : ` (${t("caseDashboard.connections.off")})`
                      }`
                  )
                  .join(" · ")}
              </p>
            )}
            <p className="mt-1 text-[11px] text-[#9CA3AF]">
              {t("caseDashboard.connections.wearablesNote")}
            </p>
          </>
        )}
      </section>
    </div>
  );
};

export default ConnectionsCard;
